import React from 'react'
import PropTypes from 'prop-types'
import Text from '../../elementals/text'
import Icon from '../../elementals/icon'
import PopupTooltip from '../../overlays/popupTooltip'
import Button from '../../elementals/button'
import MaskedInputField from '../../formFields/maskedInputField'
import { Loader } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import _ from 'lodash'
import { phoneNumberMask } from '../utils'
import styles from './styles.scss'
const cx = classnames.bind(styles)

export const DEFAULT_PHONE_INPUT_HELP_TEXT =
  'Verify your new phone number in order to update.'

const OPTIN_HELP_TEXT =
  'By providing your phone number, you agree to receive text messages related to your transactions and account activity. Message and data rates may apply. Reply STOP to opt out at any time.'

/*
 * This component receives props from the PhoneVerification container.
 * It is responsible for the UI & UX of the phone verification form.
 * It has 3 main states:
 *   1. Has verified phone number (view-only mode) -> display the verified phone number.
 *   2. Has verified phone number (update mode) -> prompt the user to verify a phone number.
 *   3. Has no verified phone number -> prompt the user to verify a phone number.
 */
const PhoneVerificationForm = ({
  isCodeSent,
  isSendingCode,
  isToggled,
  isVerifyingCode,
  latestPhone,
  phoneError,
  phoneInputHelpText = DEFAULT_PHONE_INPUT_HELP_TEXT,
  renderCodeInputField,
  renderPhoneInputField,
  sendCodeError,
  sendCodeViaCall,
  sendCodeViaSMS,
  setToggleWithReset,
  showOptInHelpText = false,
  verifyCodeError,
  compact,
  onDeleteClick,
}) => {
  const isLatestPhoneVerified = _.get(latestPhone, 'verified', false)
  const latestPhoneNumber = _.get(latestPhone, 'number', null)
  const submitError = sendCodeError || verifyCodeError
  const hasPhoneError = Boolean(phoneError)
  const shouldPromptCode = !hasPhoneError && isCodeSent
  const disableButtons = isSendingCode || isVerifyingCode || hasPhoneError

  return (
    <div className='ui form'>
      <div className={cx('u-mb-2')}>
        <Text as='span' color='gray' content='Phone Number' weight='heavy' />
        {isLatestPhoneVerified && (
          <Text
            as='span'
            className={cx('u-ml-2', 'link')}
            color='info'
            content={isToggled ? 'Cancel' : 'Update'}
            data-elm-id={isToggled ? 'cancel_update_toggle' : 'update_toggle'}
            onClick={() => setToggleWithReset(!isToggled)}
          />
        )}
        {latestPhoneNumber && onDeleteClick && !isToggled && (
          <Text
            as='span'
            className={cx('u-ml-2', 'link')}
            color='caution'
            content='Delete'
            data-elm-id='delete_number'
            onClick={() => onDeleteClick(latestPhone)}
          />
        )}
        {(!isLatestPhoneVerified || isToggled) &&
          !_.isEmpty(phoneInputHelpText) && (
            <div className={cx('u-mt-1')}>
              <AccountInfoHelpText content={phoneInputHelpText} />
            </div>
          )}
      </div>

      {isLatestPhoneVerified && !isToggled ? (
        <div className={cx('phone-input-container')}>
          <MaskedInputField
            disabled
            elmId='verify_phone'
            hideLabel
            inputLabel='+1'
            mask={phoneNumberMask}
            value={latestPhoneNumber}
            compact={compact}
          />
          <Text
            data-elm-id='verified_phone_label'
            className={cx('success-text')}
            content='Verified'
            color='success'
          />
          {showOptInHelpText && (
            <Text
              data-elm-id='opt_in_help_text'
              as='span'
              color='gray'
              size='tiny'
              content={OPTIN_HELP_TEXT}
            />
          )}
        </div>
      ) : (
        <>
          <div className={cx('phone-input-container')}>
            {renderPhoneInputField}
            {!isSendingCode && shouldPromptCode && (
              <Text
                className={cx('success-text')}
                content='Code Sent'
                color='success'
              />
            )}
            {isSendingCode && (
              <div className={cx('loader')}>
                <Loader size='small' active inline />
              </div>
            )}
            {showOptInHelpText && (
              <Text
                data-elm-id='opt_in_help_text'
                as='span'
                color='gray'
                size='tiny'
                content={OPTIN_HELP_TEXT}
              />
            )}
          </div>

          <div className={cx('button-container')}>
            <Button
              disabled={disableButtons}
              elmId='send_via_sms_button'
              onClick={sendCodeViaSMS}
              role='secondary'
              size='medium'
              type='button'
            >
              {isCodeSent ? 'Resend via SMS' : 'Send via SMS'}
            </Button>
            <Button
              disabled={disableButtons}
              elmId='send_via_call_button'
              onClick={sendCodeViaCall}
              role='secondary'
              size='medium'
              type='button'
            >
              {isCodeSent ? 'Resend via Call' : 'Send via Call'}
            </Button>
          </div>
          {shouldPromptCode && (
            <div
              className={cx('code-input-container', !submitError && 'u-mb-3')}
            >
              <Text
                as='p'
                color='gray'
                content='Verification Code'
                weight='medium'
              />
              {renderCodeInputField}
              {isVerifyingCode && (
                <div className={cx('loader')}>
                  <Loader size='small' active inline />
                </div>
              )}
            </div>
          )}

          {submitError && (
            <Text
              data-elm-id='submit_error'
              className={cx('u-mb-3', 'submit-error')}
              color='error'
              content={submitError}
            />
          )}
        </>
      )}
    </div>
  )
}

PhoneVerificationForm.propTypes = {
  isCodeSent: PropTypes.bool,
  isSendingCode: PropTypes.bool,
  isToggled: PropTypes.bool,
  isVerifyingCode: PropTypes.bool,
  latestPhone: PropTypes.object,
  phoneInputHelpText: PropTypes.string,
  renderPhoneInputField: PropTypes.node.isRequired,
  renderCodeInputField: PropTypes.node.isRequired,
  phoneError: PropTypes.string,
  sendCodeError: PropTypes.node,
  sendCodeViaCall: PropTypes.func.isRequired,
  sendCodeViaSMS: PropTypes.func.isRequired,
  setToggleWithReset: PropTypes.func.isRequired,
  showOptInHelpText: PropTypes.bool,
  verifyCodeError: PropTypes.string,
  compact: PropTypes.bool,
  onDeleteClick: PropTypes.func,
}

export default PhoneVerificationForm

const TooltipContent = () => (
  <>
    <span>
      By providing your number and proceeding, you consent to receive an
      individual verification message from
    </span>
    <a
      href='https://www.auction.com'
      className={cx('u-ml-1', 'u-mr-1', 'auction-link')}
      target='_BLANK'
      data-elm-id='auction-link'
      rel='noreferrer'
    >
      Auction.com
    </a>
    <span>
      via call or text to the number you provided. Third-party message and data
      rates may apply. Texting “Stop” won’t impact other communications we make.
    </span>
  </>
)

const AccountInfoHelpText = props => (
  <>
    <Text as='span' color='gray' {...props} className='u-mr-1' />
    <PopupTooltip
      content={<TooltipContent />}
      trigger={<Icon name='info circle' fitted />}
    />
  </>
)

AccountInfoHelpText.propTypes = {
  content: PropTypes.string,
}
